import {
  buildLegacyMucusSummary,
  MUCUS_COLOR_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS_BASIC,
  MUCUS_FREQUENCY_OPTIONS,
} from "../cycleLogic";
import { roundTemperature, TEMPERATURE_F_MAX, TEMPERATURE_F_MIN } from "../temperature";
import { unescapeSpreadsheetText } from "./csv";
import { OBSERVATION_CSV_COLUMNS, type ObservationCsvField } from "./observationsCsv";

/**
 * CSV import of observations: column auto-detection, value normalization to the DB's
 * CHECK-constrained vocabularies, per-row validation and the upsert plan. Pure — the UI
 * (CsvImportDialog) owns file reading, the existing-dates lookup and the actual upsert.
 */

/** Fields a CSV column can be mapped onto. `cycle_day` is derived, so it is recognized but never written. */
export type ObservationImportField = Exclude<ObservationCsvField, "cycle_day">;

export const OBSERVATION_IMPORT_FIELDS: readonly { field: ObservationImportField; label: string }[] =
  OBSERVATION_CSV_COLUMNS.filter(
    (c): c is { field: ObservationImportField; header: string } => c.field !== "cycle_day"
  ).map((c) => ({ field: c.field, label: c.header.replace(" (°F)", "") }));

/** One entry per CSV column: the field it feeds, or null to ignore the column. */
export type ColumnMapping = (ObservationImportField | null)[];

/** "iso" accepts only year-first dates (NFP Tracker exports); the others also read slash dates. */
export type ImportDateFormat = "iso" | "ymd" | "mdy" | "dmy";
export type TemperatureUnit = "F" | "C";
export type ImportConflictPolicy = "skip" | "overwrite";

/**
 * A named set of header aliases for a specific source app's export. Only formats we can verify
 * belong here; everything else goes through the generic synonyms in HEADER_ALIASES.
 */
export interface ObservationImportPreset {
  id: string;
  label: string;
  /** Header aliases (matched after normalizeHeader) per field. */
  headers: Partial<Record<ObservationCsvField, readonly string[]>>;
  dateFormat?: ImportDateFormat;
  temperatureUnit?: TemperatureUnit;
}

export const NFP_TRACKER_EXPORT_PRESET: ObservationImportPreset = {
  id: "nfp-tracker",
  label: "This app's CSV export",
  headers: Object.fromEntries(OBSERVATION_CSV_COLUMNS.map((c) => [c.field, [c.header]])),
  dateFormat: "ymd",
  temperatureUnit: "F",
};

export const OBSERVATION_IMPORT_PRESETS: readonly ObservationImportPreset[] = [NFP_TRACKER_EXPORT_PRESET];

/** Header row of the CSV export before 0.5.0 (unquoted cells, no temperature/cycle start). */
export const LEGACY_NFP_TRACKER_EXPORT_HEADERS = [
  "Date",
  "Cycle Day",
  "Bleeding",
  "Monitor",
  "Ovulation Test",
  "Intercourse",
  "Mucus",
  "Symptoms",
  "Notes",
] as const;

export type NfpTrackerExportCheck =
  | { status: "accepted"; mapping: ColumnMapping; ignoredColumns: string[] }
  | { status: "rejected"; reason: "legacy-export" | "not-nfp-tracker" | "duplicate-columns"; missing: string[] };

/**
 * Accept only files exported by this app (the release-gated import surface). Every current
 * export header must be present exactly once (matched case- and whitespace-insensitively, any
 * order); unknown extra columns — e.g. one a user added in Excel — are ignored and reported.
 *
 * Exports from before 0.5.0 are rejected rather than imported: they wrote unquoted cells, so a
 * mucus value like "Stretchy, Clear" shifted every later column and the rows can't be trusted.
 */
export const checkNfpTrackerExportHeaders = (headers: readonly string[]): NfpTrackerExportCheck => {
  const key = (h: string) => h.trim().replace(/\s+/g, " ").toLowerCase();
  const keys = headers.map(key);
  const expected = OBSERVATION_CSV_COLUMNS.map((c) => ({ ...c, key: key(c.header) }));
  const missing = expected.filter((c) => !keys.includes(c.key)).map((c) => c.header);

  if (missing.length > 0) {
    const legacyKeys = LEGACY_NFP_TRACKER_EXPORT_HEADERS.map(key);
    const isLegacy =
      keys.length === legacyKeys.length && legacyKeys.every((k, i) => keys[i] === k);
    return { status: "rejected", reason: isLegacy ? "legacy-export" : "not-nfp-tracker", missing };
  }
  if (expected.some((c) => keys.filter((k) => k === c.key).length > 1)) {
    return { status: "rejected", reason: "duplicate-columns", missing: [] };
  }

  const fieldByKey = new Map(expected.map((c) => [c.key, c.field]));
  const ignoredColumns: string[] = [];
  const mapping: ColumnMapping = keys.map((k, i) => {
    const field = fieldByKey.get(k);
    if (!field) {
      if (headers[i].trim()) ignoredColumns.push(headers[i].trim());
      return null;
    }
    return field === "cycle_day" ? null : field;
  });
  return { status: "accepted", mapping, ignoredColumns };
};

/**
 * Generic header synonyms, used for files from spreadsheets or other apps.
 * Internal/future: the 0.5.0 UI only accepts NFP Tracker exports (checkNfpTrackerExportHeaders);
 * detectColumnMapping, date-order/°C detection and inferCycleStartDates are kept, tested, for
 * adding verified presets for other apps later.
 */
const HEADER_ALIASES: Record<ObservationCsvField, readonly string[]> = {
  observation_date: ["date", "day", "observation date", "entry date", "calendar date"],
  cycle_day: ["cycle day", "cd", "day of cycle", "cycle day number"],
  is_cycle_start: ["cycle start", "new cycle", "period start", "cycle start day", "first day of cycle", "start of cycle"],
  temperature_f: ["temperature", "temp", "bbt", "basal body temperature", "basal temperature", "waking temperature"],
  bleeding: ["bleeding", "period", "menstruation", "flow", "menses", "period flow", "menstrual flow"],
  mucus_consistency: ["mucus consistency", "consistency", "cm consistency", "cervical mucus consistency"],
  mucus_color: ["mucus color", "mucus colour", "color", "colour", "cm color", "cm colour", "mucus appearance"],
  mucus_frequency: ["mucus frequency", "frequency", "cm frequency"],
  mucus: ["mucus summary", "mucus", "cervical mucus", "cm", "cervical fluid", "discharge"],
  monitor: ["monitor", "fertility monitor", "monitor reading", "clearblue", "cbfm"],
  ovulation_test: ["ovulation test", "opk", "lh", "lh test", "ovulation test result", "opk result"],
  intercourse: ["intercourse", "sex", "bd", "intimacy", "had sex"],
  symptoms: ["symptoms", "symptom"],
  notes: ["notes", "note", "comments", "comment", "memo"],
};

const UNIT_TOKENS: Record<string, TemperatureUnit> = {
  f: "F",
  fahrenheit: "F",
  c: "C",
  celsius: "C",
  centigrade: "C",
};

/** Lowercase, drop punctuation/degree signs, collapse whitespace: "Temperature (°F)" → "temperature f". */
export const normalizeHeader = (header: string): string =>
  header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const stripUnitSuffix = (normalized: string): { base: string; unit: TemperatureUnit | null } => {
  const tokens = normalized.split(" ");
  const last = tokens[tokens.length - 1];
  if (tokens.length > 1 && UNIT_TOKENS[last]) {
    return { base: tokens.slice(0, -1).join(" "), unit: UNIT_TOKENS[last] };
  }
  return { base: normalized, unit: null };
};

export interface DetectedColumns {
  mapping: ColumnMapping;
  /** Column index of a recognized-but-derived Cycle Day column, which is ignored on import. */
  cycleDayColumn: number | null;
  /** Unit stated in the temperature header, e.g. "Temp (°C)". */
  temperatureHeaderUnit: TemperatureUnit | null;
  /** True when every header matched this app's own export exactly. */
  isOwnExport: boolean;
}

/** Map header cells onto fields. Each field is claimed by at most one column (leftmost wins). */
export const detectColumnMapping = (
  headers: readonly string[],
  presets: readonly ObservationImportPreset[] = OBSERVATION_IMPORT_PRESETS
): DetectedColumns => {
  const aliasToField = new Map<string, ObservationCsvField>();
  const register = (field: ObservationCsvField, alias: string) => {
    const key = normalizeHeader(alias);
    if (!aliasToField.has(key)) aliasToField.set(key, field);
  };
  for (const preset of presets) {
    for (const [field, aliases] of Object.entries(preset.headers)) {
      for (const alias of aliases ?? []) register(field as ObservationCsvField, alias);
    }
  }
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    for (const alias of aliases) register(field as ObservationCsvField, alias);
  }

  const claimed = new Set<ObservationCsvField>();
  const mapping: ColumnMapping = [];
  let cycleDayColumn: number | null = null;
  let temperatureHeaderUnit: TemperatureUnit | null = null;

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    const { base, unit } = stripUnitSuffix(normalized);
    const field = aliasToField.get(normalized) ?? aliasToField.get(base) ?? null;
    if (!field || claimed.has(field)) {
      mapping.push(null);
      return;
    }
    claimed.add(field);
    if (field === "cycle_day") {
      cycleDayColumn = index;
      mapping.push(null);
      return;
    }
    if (field === "temperature_f") temperatureHeaderUnit = unit;
    mapping.push(field);
  });

  const ownHeaders = OBSERVATION_CSV_COLUMNS.map((c) => c.header);
  const isOwnExport =
    headers.length === ownHeaders.length && headers.every((h, i) => h.trim() === ownHeaders[i]);

  return { mapping, cycleDayColumn, temperatureHeaderUnit, isOwnExport };
};

// ---------------------------------------------------------------------------------------------
// Dates

const ISO_DATE = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/;
const NUMERIC_DATE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?:[T\s,].*)?$/;

const toDateKey = (year: number, month: number, day: number): string | null => {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

/**
 * Parse a date cell to `yyyy-MM-dd`. Year-first dates are always read as Y-M-D; other numeric
 * dates follow `format` (month/day order). Two-digit years are read as 20xx. Null if invalid.
 */
export const parseImportDate = (raw: string, format: ImportDateFormat): string | null => {
  const value = raw.trim();
  const iso = ISO_DATE.exec(value);
  if (iso) return toDateKey(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  if (format === "iso") return null;
  const numeric = NUMERIC_DATE.exec(value);
  if (!numeric) return null;
  const first = Number(numeric[1]);
  const second = Number(numeric[2]);
  const year = numeric[3].length === 2 ? 2000 + Number(numeric[3]) : Number(numeric[3]);
  return format === "dmy" ? toDateKey(year, second, first) : toDateKey(year, first, second);
};

export interface DateFormatDetection {
  format: ImportDateFormat;
  /** True when day/month order can't be told from the data — the user must choose. */
  ambiguous: boolean;
  /** True when the column has day-first and month-first evidence at once. */
  conflicting: boolean;
}

export const detectDateFormat = (values: readonly string[]): DateFormatDetection => {
  let numericCount = 0;
  let firstOver12 = false;
  let secondOver12 = false;
  for (const raw of values) {
    const value = raw.trim();
    if (ISO_DATE.test(value)) continue;
    const match = NUMERIC_DATE.exec(value);
    if (!match) continue;
    numericCount++;
    if (Number(match[1]) > 12) firstOver12 = true;
    if (Number(match[2]) > 12) secondOver12 = true;
  }
  if (numericCount === 0) return { format: "ymd", ambiguous: false, conflicting: false };
  if (firstOver12 && secondOver12) return { format: "mdy", ambiguous: false, conflicting: true };
  if (firstOver12) return { format: "dmy", ambiguous: false, conflicting: false };
  if (secondOver12) return { format: "mdy", ambiguous: false, conflicting: false };
  return { format: "mdy", ambiguous: true, conflicting: false };
};

// ---------------------------------------------------------------------------------------------
// Temperatures

const CELSIUS_BBT_MIN = 34;
const CELSIUS_BBT_MAX = 39;

/** Parse "97.53", "97,53", "97.5°F", "36.4 C" etc. to a number (no unit conversion). */
export const parseTemperatureNumber = (raw: string): number | null => {
  let value = raw.trim().replace(/\s*°?\s*[cf]$/i, "").replace(/°$/, "").trim();
  if (!value) return null;
  if (!value.includes(".") && /^\d+,\d+$/.test(value)) value = value.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(value)) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const celsiusToFahrenheit = (celsius: number): number => roundTemperature((celsius * 9) / 5 + 32);

/**
 * Suggest a unit for a temperature column: the header's stated unit if any, otherwise °C when
 * the median reading sits in a plausible Celsius BBT band (34–39). The UI shows this as a
 * visible, user-editable toggle — it is a suggestion, never applied silently.
 */
export const suggestTemperatureUnit = (
  values: readonly string[],
  headerUnit: TemperatureUnit | null
): { unit: TemperatureUnit; reason: "header" | "values" | "default" } => {
  if (headerUnit) return { unit: headerUnit, reason: "header" };
  const numbers = values
    .map(parseTemperatureNumber)
    .filter((n): n is number => n !== null)
    .sort((a, b) => a - b);
  if (numbers.length === 0) return { unit: "F", reason: "default" };
  const median = numbers[Math.floor(numbers.length / 2)];
  if (median >= CELSIUS_BBT_MIN && median <= CELSIUS_BBT_MAX) return { unit: "C", reason: "values" };
  return { unit: "F", reason: "default" };
};

// ---------------------------------------------------------------------------------------------
// Value normalizers. Each returns the DB value (null for blank) or `invalid` for text that
// can't be understood, which becomes a per-row warning and leaves the field empty.

type Normalized<T> = { value: T | null } | { invalid: true };
const INVALID = { invalid: true } as const;

const squash = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const TRUE_WORDS = new Set(["yes", "y", "true", "t", "1", "x", "on", "checked", "✓", "✔"]);
const FALSE_WORDS = new Set(["no", "n", "false", "f", "0", "off", "unchecked"]);

export const normalizeImportBoolean = (raw: string): Normalized<boolean> => {
  const value = raw.trim().toLowerCase();
  if (!value) return { value: null };
  if (TRUE_WORDS.has(value)) return { value: true };
  if (FALSE_WORDS.has(value)) return { value: false };
  return INVALID;
};

const BLEEDING_WORDS: Record<string, number> = {
  verylight: 4,
  spotting: 4,
  spot: 4,
  light: 1,
  medium: 2,
  moderate: 2,
  heavy: 3,
  brown: 5,
  brownspotting: 5,
};
const NO_BLEEDING_WORDS = new Set(["none", "no", "0", "dry", "false"]);

/** Bleeding to the DB code 1–5 (CHECK constraint): accepts the codes themselves or their labels. */
export const normalizeImportBleeding = (raw: string): Normalized<number> => {
  const value = raw.trim();
  if (!value) return { value: null };
  if (/^[1-5]$/.test(value)) return { value: Number(value) };
  if (NO_BLEEDING_WORDS.has(value.toLowerCase())) return { value: null };
  const code = BLEEDING_WORDS[squash(value)];
  return code ? { value: code } : INVALID;
};

/** Monitor reading to 'LOW' | 'HIGH' | 'PEAK' (CHECK constraint). */
export const normalizeImportMonitor = (raw: string): Normalized<string> => {
  const value = raw.trim().toLowerCase();
  if (!value) return { value: null };
  if (value === "low" || value === "l") return { value: "LOW" };
  if (value === "high" || value === "h") return { value: "HIGH" };
  if (value === "peak" || value === "p") return { value: "PEAK" };
  return INVALID;
};

/** Ovulation test to 'positive' | 'negative' (CHECK constraint). */
export const normalizeImportOvulationTest = (raw: string): Normalized<string> => {
  const value = raw.trim().toLowerCase();
  if (!value) return { value: null };
  if (["positive", "pos", "+", "yes", "y", "true", "1", "surge", "peak"].includes(value)) {
    return { value: "positive" };
  }
  if (["negative", "neg", "-", "no", "n", "false", "0"].includes(value)) return { value: "negative" };
  return INVALID;
};

/** Mucus frequency to '1x' | '2x' | '3x' | 'AD' (CHECK constraint). */
export const normalizeImportMucusFrequency = (raw: string): Normalized<string> => {
  const value = squash(raw);
  if (!value) return { value: null };
  if (value === "allday" || value === "ad") return { value: "AD" };
  const match = /^([123])x?$/.exec(value);
  if (match) return { value: `${match[1]}x` };
  const option = MUCUS_FREQUENCY_OPTIONS.find((o) => squash(o.value) === value || squash(o.label) === value);
  return option ? { value: option.value } : INVALID;
};

const CONSISTENCY_OPTIONS = [...MUCUS_CONSISTENCY_OPTIONS, ...MUCUS_CONSISTENCY_OPTIONS_BASIC];

const matchOption = (
  raw: string,
  options: readonly { value: string; label: string }[]
): string | null => {
  const key = squash(raw);
  if (!key) return null;
  return (
    options.find((o) => squash(o.value) === key)?.value ??
    options.find((o) => squash(o.label) === key)?.value ??
    // "Dry / None" and "Pasty (Creamy)" style labels: accept either part.
    options.find((o) => o.label.split(/[/()]/).some((part) => squash(part) === key))?.value ??
    null
  );
};

export const matchMucusConsistency = (raw: string): string | null => matchOption(raw, CONSISTENCY_OPTIONS);
export const matchMucusColor = (raw: string): string | null => matchOption(raw, MUCUS_COLOR_OPTIONS);

/** Split a Symptoms cell ("Cramps; Headache", "Cramps, Headache" or "Cramps|Headache"). */
export const splitSymptoms = (raw: string, knownSymptoms: readonly string[] = []): string[] | null => {
  const value = raw.trim();
  if (!value) return null;
  const separator = /[;|]/.test(value) ? /[;|]/ : /,/;
  const canonical = new Map(knownSymptoms.map((s) => [s.toLowerCase(), s]));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of value.split(separator)) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const name = canonical.get(trimmed.toLowerCase()) ?? trimmed;
    if (seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result.length ? result : null;
};

// ---------------------------------------------------------------------------------------------
// Row analysis

/** DB columns an import writes. Every planned upsert row carries exactly the same keys. */
export type ObservationImportColumn =
  | "is_cycle_start"
  | "temperature_f"
  | "bleeding"
  | "mucus"
  | "mucus_consistency"
  | "mucus_color"
  | "mucus_frequency"
  | "monitor"
  | "ovulation_test"
  | "intercourse"
  | "symptoms"
  | "notes";

export interface ObservationImportValues {
  observation_date: string;
  is_cycle_start?: boolean;
  temperature_f?: number | null;
  bleeding?: number | null;
  mucus?: string | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  mucus_frequency?: string | null;
  monitor?: string | null;
  ovulation_test?: string | null;
  intercourse?: boolean | null;
  symptoms?: string[] | null;
  notes?: string | null;
}

export interface ObservationImportRow {
  /** 1-based line number in the file (the header is line 1). */
  line: number;
  date: string | null;
  values: ObservationImportValues | null;
  /** Fatal problems: the row is not imported. */
  errors: string[];
  /** Non-fatal problems: the row is imported, minus the offending value. */
  warnings: string[];
  /**
   * Columns whose cell had a value that couldn't be understood (as opposed to a blank cell).
   * New days store null for these; when overwriting an existing day they're left out of the
   * write so the day's current value is kept.
   */
  invalidColumns: ObservationImportColumn[];
}

export interface ObservationImportOptions {
  mapping: ColumnMapping;
  dateFormat: ImportDateFormat;
  temperatureUnit: TemperatureUnit;
  /** Canonical symptom names, so "cramps" imports as the app's "Cramps". */
  knownSymptoms?: readonly string[];
  /** yyyy-MM-dd; rows after this date are rejected. Omit to allow any date. */
  today?: string;
  /**
   * When no column is mapped to Cycle Start, mark the first day of each period (see
   * inferCycleStartDates) so imported history splits into cycles instead of one long one.
   */
  inferCycleStarts?: boolean;
  /**
   * Keep mucus consistency/color values that aren't one of the app's options verbatim instead
   * of folding them into the free-text summary. Used for NFP Tracker exports, whose values came
   * straight from the (free-text) DB columns and must round-trip unchanged.
   */
  preserveMucusValues?: boolean;
}

export interface ObservationImportAnalysis {
  rows: ObservationImportRow[];
  validRows: ObservationImportRow[];
  columns: ObservationImportColumn[];
  dateRange: { from: string; to: string } | null;
  errorCount: number;
  warningCount: number;
  inferredCycleStartCount: number;
}

/** Mucus columns are written together so the legacy summary never disagrees with its parts. */
const MUCUS_COLUMNS: ObservationImportColumn[] = ["mucus", "mucus_consistency", "mucus_color"];

export const importColumnsForMapping = (
  mapping: ColumnMapping,
  inferCycleStarts = false
): ObservationImportColumn[] => {
  const mapped = new Set(mapping.filter((f): f is ObservationImportField => f !== null));
  const columns: ObservationImportColumn[] = [];
  if (mapped.has("is_cycle_start") || inferCycleStarts) columns.push("is_cycle_start");
  if (mapped.has("temperature_f")) columns.push("temperature_f");
  if (mapped.has("bleeding")) columns.push("bleeding");
  if (mapped.has("mucus") || mapped.has("mucus_consistency") || mapped.has("mucus_color")) {
    columns.push(...MUCUS_COLUMNS);
  }
  const simple: ObservationImportColumn[] = [
    "mucus_frequency",
    "monitor",
    "ovulation_test",
    "intercourse",
    "symptoms",
    "notes",
  ];
  for (const column of simple) if (mapped.has(column)) columns.push(column);
  return columns;
};

const FIELD_LABEL = Object.fromEntries(OBSERVATION_IMPORT_FIELDS.map((f) => [f.field, f.label])) as Record<
  ObservationImportField,
  string
>;

const truncate = (value: string, max = 40) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

const analyzeRow = (
  cells: readonly string[],
  line: number,
  options: ObservationImportOptions
): ObservationImportRow => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const invalidColumns: ObservationImportColumn[] = [];
  const cell = (field: ObservationImportField): string | undefined => {
    const index = options.mapping.indexOf(field);
    return index === -1 ? undefined : (cells[index] ?? "");
  };
  const markInvalid = (field: ObservationImportColumn, message: string) => {
    invalidColumns.push(field);
    warnings.push(`${message}; not imported (an existing entry for this day keeps its value)`);
  };
  const invalid = (field: ObservationImportColumn, raw: string) =>
    markInvalid(field, `${FIELD_LABEL[field]} "${truncate(raw.trim())}" not recognized`);

  const rawDate = cell("observation_date") ?? "";
  const date = rawDate.trim() ? parseImportDate(rawDate, options.dateFormat) : null;
  if (!rawDate.trim()) errors.push("Missing date");
  else if (!date && options.dateFormat === "iso" && NUMERIC_DATE.test(rawDate.trim())) {
    errors.push(
      `Date "${truncate(rawDate.trim())}" isn't in YYYY-MM-DD format (a spreadsheet app may have reformatted it when saving; re-export from NFP Tracker)`
    );
  } else if (!date) errors.push(`Date "${truncate(rawDate.trim())}" not recognized`);
  else if (options.today && date > options.today) errors.push(`Date ${date} is in the future`);

  const values: ObservationImportValues = { observation_date: date ?? "" };

  const cycleStart = cell("is_cycle_start");
  if (cycleStart !== undefined) {
    const result = normalizeImportBoolean(cycleStart);
    if ("invalid" in result) invalid("is_cycle_start", cycleStart);
    values.is_cycle_start = "invalid" in result ? false : result.value ?? false;
  } else if (options.inferCycleStarts) {
    values.is_cycle_start = false;
  }

  const temperature = cell("temperature_f");
  if (temperature !== undefined) {
    values.temperature_f = null;
    if (temperature.trim()) {
      const parsed = parseTemperatureNumber(temperature);
      if (parsed === null) invalid("temperature_f", temperature);
      else {
        const fahrenheit = options.temperatureUnit === "C" ? celsiusToFahrenheit(parsed) : roundTemperature(parsed);
        if (fahrenheit < TEMPERATURE_F_MIN || fahrenheit > TEMPERATURE_F_MAX) {
          markInvalid(
            "temperature_f",
            `Temperature ${temperature.trim()} is outside ${TEMPERATURE_F_MIN}–${TEMPERATURE_F_MAX} °F${
              options.temperatureUnit === "F" && parsed >= CELSIUS_BBT_MIN && parsed <= CELSIUS_BBT_MAX
                ? " (is this column in °C?)"
                : ""
            }`
          );
        } else {
          values.temperature_f = fahrenheit;
        }
      }
    }
  }

  const applySimple = <T>(
    field: ObservationImportColumn,
    normalize: (raw: string) => Normalized<T>,
    assign: (value: T | null) => void
  ) => {
    const raw = cell(field);
    if (raw === undefined) return;
    const result = normalize(raw);
    if ("invalid" in result) {
      invalid(field, raw);
      assign(null);
    } else {
      assign(result.value);
    }
  };

  applySimple("bleeding", normalizeImportBleeding, (v) => (values.bleeding = v));
  applySimple("mucus_frequency", normalizeImportMucusFrequency, (v) => (values.mucus_frequency = v));
  applySimple("monitor", normalizeImportMonitor, (v) => (values.monitor = v));
  applySimple("ovulation_test", normalizeImportOvulationTest, (v) => (values.ovulation_test = v));
  applySimple("intercourse", normalizeImportBoolean, (v) => (values.intercourse = v));

  // Mucus. Consistency/color map onto the app's option vocabularies; text that doesn't match
  // is never dropped — it's kept in the free-text legacy `mucus` summary with a warning.
  const rawConsistency = cell("mucus_consistency");
  const rawColor = cell("mucus_color");
  const rawSummary = cell("mucus");
  if (rawConsistency !== undefined || rawColor !== undefined || rawSummary !== undefined) {
    const unmatched: string[] = [];
    const match = (
      raw: string | undefined,
      matcher: (text: string) => string | null,
      label: string
    ): string | null => {
      const text = unescapeSpreadsheetText((raw ?? "").trim());
      if (!text) return null;
      const matched = matcher(text);
      if (!matched && options.preserveMucusValues) {
        warnings.push(`${label} "${truncate(text)}" isn't one of the app's options; kept as-is`);
        return text;
      }
      if (!matched) {
        unmatched.push(text);
        warnings.push(`${label} "${truncate(text)}" not recognized; kept as mucus text`);
      }
      return matched;
    };
    let consistency = match(rawConsistency, matchMucusConsistency, "Mucus consistency");
    let color = match(rawColor, matchMucusColor, "Mucus color");

    const summaryText = unescapeSpreadsheetText((rawSummary ?? "").trim());
    let mucus: string | null;
    if (rawConsistency === undefined && rawColor === undefined && summaryText) {
      // Only a single free-text mucus column: recognize "Stretchy" or "Stretchy, Clear".
      const parts = summaryText.split(",").map((p) => p.trim());
      const inferredConsistency = parts[0] ? matchMucusConsistency(parts[0]) : null;
      const inferredColor = parts.length === 2 ? matchMucusColor(parts[1]) : null;
      if (inferredConsistency && (parts.length === 1 || inferredColor)) {
        consistency = inferredConsistency;
        color = inferredColor;
        mucus = buildLegacyMucusSummary(consistency, color ?? "");
      } else {
        mucus = summaryText;
        warnings.push(`Mucus "${truncate(summaryText)}" didn't match a mucus option; kept as text`);
      }
    } else if (summaryText) {
      // Our own export (or a file with explicit parts): the summary column is authoritative.
      mucus = summaryText;
    } else {
      const summary = buildLegacyMucusSummary(consistency ?? "", color ?? "");
      mucus = [summary, ...unmatched].filter(Boolean).join(", ") || null;
    }
    values.mucus = mucus;
    values.mucus_consistency = consistency;
    values.mucus_color = color;
  }

  const symptoms = cell("symptoms");
  if (symptoms !== undefined) {
    values.symptoms = splitSymptoms(unescapeSpreadsheetText(symptoms.trim()), options.knownSymptoms);
  }

  const notes = cell("notes");
  if (notes !== undefined) {
    values.notes = notes.trim() ? unescapeSpreadsheetText(notes) : null;
  }

  return { line, date, values: errors.length ? null : values, errors, warnings, invalidColumns };
};

/**
 * First day of each period: a light/medium/heavy bleeding day (not very-light or brown
 * spotting) whose previous calendar day had no such bleeding, at least 14 days after the last
 * start (so a short mid-cycle bleed doesn't split a cycle). Dates must be yyyy-MM-dd.
 */
export const inferCycleStartDates = (
  days: readonly { observation_date: string; bleeding?: number | null }[]
): Set<string> => {
  const FLOW = new Set([1, 2, 3]);
  const MIN_GAP_DAYS = 14;
  const dayNumber = (date: string) => Date.parse(`${date}T00:00:00Z`) / 86_400_000;
  const flowDays = new Set(days.filter((d) => d.bleeding != null && FLOW.has(d.bleeding)).map((d) => d.observation_date));
  const sorted = [...flowDays].sort();
  const starts = new Set<string>();
  let lastStart: number | null = null;
  for (const date of sorted) {
    const n = dayNumber(date);
    const previous = new Date((n - 1) * 86_400_000).toISOString().slice(0, 10);
    if (flowDays.has(previous)) continue;
    if (lastStart !== null && n - lastStart < MIN_GAP_DAYS) continue;
    starts.add(date);
    lastStart = n;
  }
  return starts;
};

/**
 * Validate every data row (`table` includes the header row) against the mapping. Duplicate
 * dates keep the first occurrence; later ones are errors.
 */
export const analyzeObservationImport = (
  table: readonly (readonly string[])[],
  options: ObservationImportOptions
): ObservationImportAnalysis => {
  const cycleStartMapped = options.mapping.includes("is_cycle_start");
  const inferCycleStarts = Boolean(options.inferCycleStarts) && !cycleStartMapped;
  const effective = { ...options, inferCycleStarts };

  const rows: ObservationImportRow[] = [];
  if (!options.mapping.includes("observation_date")) {
    return {
      rows,
      validRows: [],
      columns: [],
      dateRange: null,
      errorCount: 0,
      warningCount: 0,
      inferredCycleStartCount: 0,
    };
  }

  const seen = new Map<string, number>();
  table.slice(1).forEach((cells, index) => {
    const row = analyzeRow(cells, index + 2, effective);
    if (row.date && row.values) {
      const firstLine = seen.get(row.date);
      if (firstLine !== undefined) {
        row.errors.push(`Duplicate date ${row.date} (already on line ${firstLine})`);
        row.values = null;
      } else {
        seen.set(row.date, row.line);
      }
    }
    rows.push(row);
  });

  const validRows = rows.filter((r) => r.values !== null);

  let inferredCycleStartCount = 0;
  if (inferCycleStarts) {
    const starts = inferCycleStartDates(validRows.map((r) => r.values as ObservationImportValues));
    for (const row of validRows) {
      if (row.values && starts.has(row.values.observation_date)) {
        row.values.is_cycle_start = true;
        inferredCycleStartCount++;
      }
    }
  }

  const dates = validRows.map((r) => r.values!.observation_date).sort();
  return {
    rows,
    validRows,
    columns: importColumnsForMapping(options.mapping, inferCycleStarts),
    dateRange: dates.length ? { from: dates[0], to: dates[dates.length - 1] } : null,
    errorCount: rows.filter((r) => r.errors.length > 0).length,
    warningCount: rows.reduce((sum, r) => sum + r.warnings.length, 0),
    inferredCycleStartCount,
  };
};

export type ObservationUpsertRow = {
  owner_user_id: string;
  observation_date: string;
  /** Omitted when overwriting an existing day, so the original creator is preserved. */
  created_by_user_id?: string | null;
} & Partial<Record<ObservationImportColumn, unknown>>;

export interface ObservationImportPlan {
  rows: ObservationUpsertRow[];
  newCount: number;
  overwriteCount: number;
  skippedExistingCount: number;
}

/**
 * Build the upsert payload. With "skip", days that already have an observation are left alone.
 * With "overwrite", an existing day gets only the file's columns, minus any cell that was
 * invalid (so a typo never wipes a good value), and keeps its original `created_by_user_id`.
 * Rows therefore don't all share one key set — send them with `batchObservationUpsertRows`,
 * because a PostgREST bulk upsert writes the union of keys and would null the missing ones.
 */
export const planObservationImport = (
  analysis: ObservationImportAnalysis,
  params: {
    ownerUserId: string;
    createdByUserId: string | null;
    existingDates: ReadonlySet<string>;
    policy: ImportConflictPolicy;
  }
): ObservationImportPlan => {
  let newCount = 0;
  let overwriteCount = 0;
  let skippedExistingCount = 0;
  const rows: ObservationUpsertRow[] = [];

  for (const row of analysis.validRows) {
    const values = row.values!;
    const exists = params.existingDates.has(values.observation_date);
    if (exists && params.policy === "skip") {
      skippedExistingCount++;
      continue;
    }
    if (exists) overwriteCount++;
    else newCount++;

    const upsert: ObservationUpsertRow = {
      owner_user_id: params.ownerUserId,
      observation_date: values.observation_date,
    };
    if (!exists) upsert.created_by_user_id = params.createdByUserId;
    for (const column of analysis.columns) {
      if (exists && row.invalidColumns.includes(column)) continue;
      const value = values[column];
      upsert[column] = column === "is_cycle_start" ? Boolean(value) : value ?? null;
    }
    rows.push(upsert);
  }

  return { rows, newCount, overwriteCount, skippedExistingCount };
};

/**
 * Group upsert rows by their exact key set, then chunk each group. Every batch is safe to send
 * as one bulk upsert: all its rows name the same columns, so none is implicitly nulled.
 */
export const batchObservationUpsertRows = (
  rows: readonly ObservationUpsertRow[],
  size: number
): ObservationUpsertRow[][] => {
  const groups = new Map<string, ObservationUpsertRow[]>();
  for (const row of rows) {
    const signature = Object.keys(row).sort().join(",");
    const group = groups.get(signature);
    if (group) group.push(row);
    else groups.set(signature, [row]);
  }
  return [...groups.values()].flatMap((group) => chunkImportRows(group, size));
};

/** Split an array into batches for chunked upserts. */
export const chunkImportRows = <T>(items: readonly T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size));
  return batches;
};
